import { useEffect, useRef, useState, type ReactNode } from "react";
import { animate, motion, useReducedMotion } from "framer-motion";
import { ChevronDown, MoreHorizontal, Search, UserPlus } from "lucide-react";
import { Clicky, ScreenFrame, ScreenStage } from "./ScreenStage";

/*
 * A live rebuild of the Quilt Pro "Patient Management" screen. Everything reacts to hover and
 * clicks, but nothing does anything: it is a picture you can touch.
 */

type SignUp = "Completed" | "Pending";
type Status = "Active" | "Deactivated";

interface Patient {
    name: string;
    email: string;
    dob: string;
    gender: string;
    location: string;
    condition: string;
    tags: string[];
    group?: string;
    signUp: SignUp;
    lastActivity: string;
    signedUp: string;
    status: Status;
}

const PATIENTS: Patient[] = [
    { name: "Nolan Ridgeway", email: "ngridgeway@email.com", dob: "21 Jun 1983", gender: "Male", location: "Chicago, IL", condition: "Agoraphobia", tags: [], group: "Anxiety Group A", signUp: "Completed", lastActivity: "Just now", signedUp: "03 Feb 2026", status: "Active" },
    { name: "Dalton Pruett", email: "daltonpruett@email.com", dob: "14 Mar 1987", gender: "Male", location: "Boise, ID", condition: "Post-Traumatic Stress Disorder", tags: [], signUp: "Completed", lastActivity: "12 mins ago", signedUp: "12 Jan 2026", status: "Active" },
    { name: "Corine Talberth", email: "talberthcs@email.com", dob: "2 Aug 1972", gender: "Female", location: "Tucson, AZ", condition: "Depressive Disorder", tags: ["Postpartum"], group: "Sleep Reset", signUp: "Pending", lastActivity: "Invited May 12, 2026", signedUp: "03 Feb 2026", status: "Active" },
    { name: "Russell Kimbrough", email: "rskimbrough@email.com", dob: "23 Nov 1995", gender: "Male", location: "Madison, WI", condition: "Social Anxiety Disorder", tags: ["High risk"], group: "Sleep Reset", signUp: "Completed", lastActivity: "2 days ago", signedUp: "12 Jan 2026", status: "Deactivated" },
    { name: "Jane Brandvold", email: "jbrandvold@email.com", dob: "9 May 1964", gender: "Female", location: "Savannah, GA", condition: "Panic Disorder", tags: [], signUp: "Completed", lastActivity: "1 hour ago", signedUp: "03 Feb 2026", status: "Active" },
    { name: "Garrett Whitlow", email: "whitlow.gar@email.com", dob: "30 Jan 2001", gender: "Male", location: "Chattanooga, TN", condition: "Depressive Disorder", tags: [], signUp: "Completed", lastActivity: "3 days ago", signedUp: "12 Jan 2026", status: "Active" },
    { name: "Maryan Ashby", email: "maryan.a98@email.com", dob: "17 Sep 1998", gender: "Female", location: "Spokane, WA", condition: "Social Anxiety Disorder", tags: ["High risk", "Postpartum"], signUp: "Pending", lastActivity: "Invited May 19, 2026", signedUp: "03 Feb 2026", status: "Active" },
];

const COLUMNS: { label: string; width: number }[] = [
    { label: "Patient", width: 196 },
    { label: "Email", width: 216 },
    { label: "Phone", width: 166 },
    { label: "Date of Birth", width: 116 },
    { label: "Gender", width: 116 },
    { label: "Location", width: 166 },
    { label: "Conditions", width: 240 },
    { label: "Tags", width: 216 },
    { label: "Groups", width: 216 },
    { label: "Sign-up Status", width: 116 },
    { label: "Last Activity", width: 166 },
    { label: "Signed up", width: 136 },
    { label: "Status", width: 136 },
    { label: "Actions", width: 76 },
];

const FILTERS = ["Conditions", "Gender", "Location", "Tags", "Groups", "Sign-up", "Status"];

const initials = (name: string) => name.split(" ").map((part) => part[0]).join("");

function Pill({ tone, children }: { tone: "green" | "yellow" | "grey" | "teal" | "tag" | "group"; children: ReactNode }) {
    const tones = {
        green: "bg-emerald-500/10 text-emerald-600 dark:bg-[#05DF72]/10 dark:text-[#05DF72] font-medium",
        yellow: "bg-amber-400/15 text-amber-700 dark:bg-[#FCC800]/10 dark:text-[#FCC800] font-medium",
        grey: "bg-zinc-200 text-zinc-500 dark:bg-slate-700 dark:text-slate-300 font-medium",
        teal: "bg-teal-500/10 text-teal-700 dark:bg-teal-400/[0.07] dark:text-teal-400/80",
        tag: "bg-zinc-100 text-zinc-500 dark:bg-slate-800 dark:text-slate-400",
        group: "bg-zinc-100 text-zinc-800 dark:bg-slate-800 dark:text-white",
    };
    return <span className={`inline-flex h-6 items-center whitespace-nowrap rounded-full px-2.5 text-[12px] ${tones[tone]}`}>{children}</span>;
}

function Cell({ width, children, className = "" }: { width: number; children?: ReactNode; className?: string }) {
    return (
        <div className={`flex shrink-0 items-center overflow-hidden pr-4 ${className}`} style={{ width }}>
            {children}
        </div>
    );
}

const dash = <span className="text-zinc-300 dark:text-slate-700">–</span>;

function PatientRow({ p, index, show }: { p: Patient; index: number; show: boolean }) {
    return (
        <motion.div
            className="group flex h-16 cursor-pointer items-center border-t border-zinc-200/80 px-6 text-[14px] text-zinc-600 transition-colors hover:bg-zinc-50 active:bg-zinc-100 dark:border-white/[0.06] dark:text-slate-300 dark:hover:bg-purple-400/[0.04] dark:active:bg-white/[0.05]"
            initial={{ opacity: 0, y: 10 }}
            animate={show ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 + index * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
            <Cell width={COLUMNS[0].width} className="gap-2.5">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-600 dark:bg-slate-800 dark:text-slate-300">
                    {initials(p.name)}
                </span>
                <span className="truncate font-semibold text-zinc-900 dark:text-white">{p.name}</span>
            </Cell>
            <Cell width={COLUMNS[1].width} className="text-zinc-500 dark:text-slate-500">{p.email}</Cell>
            <Cell width={COLUMNS[2].width}>(555) 010-0110</Cell>
            <Cell width={COLUMNS[3].width}>{p.dob}</Cell>
            <Cell width={COLUMNS[4].width}>{p.gender}</Cell>
            <Cell width={COLUMNS[5].width}>{p.location}</Cell>
            <Cell width={COLUMNS[6].width}><Pill tone="teal">{p.condition}</Pill></Cell>
            <Cell width={COLUMNS[7].width} className="gap-1.5">{p.tags.length ? p.tags.map((t) => <Pill key={t} tone="tag">{t}</Pill>) : dash}</Cell>
            <Cell width={COLUMNS[8].width}>{p.group ? <Pill tone="group">{p.group}</Pill> : dash}</Cell>
            <Cell width={COLUMNS[9].width}><Pill tone={p.signUp === "Completed" ? "green" : "yellow"}>{p.signUp}</Pill></Cell>
            <Cell width={COLUMNS[10].width} className="gap-2 text-zinc-500 dark:text-slate-500">
                {p.lastActivity === "Just now" && (
                    <span className="relative flex size-2">
                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none" />
                        <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
                    </span>
                )}
                {p.lastActivity}
            </Cell>
            <Cell width={COLUMNS[11].width} className="text-zinc-500 dark:text-slate-500">{p.signedUp}</Cell>
            <Cell width={COLUMNS[12].width}><Pill tone={p.status === "Active" ? "green" : "grey"}>{p.status}</Pill></Cell>
            <Cell width={COLUMNS[13].width}>
                <Clicky className="flex size-8 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-zinc-200/70 hover:text-zinc-800 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white">
                    <MoreHorizontal className="size-5" />
                </Clicky>
            </Cell>
        </motion.div>
    );
}

/** The patients table: scrolls sideways by itself until someone grabs it, then it is theirs. */
function PatientTable({ show, scale }: { show: boolean; scale: number }) {
    const scroller = useRef<HTMLDivElement>(null);
    const [scroll, setScroll] = useState({ left: 0, max: 1, visible: 1 });
    const [touched, setTouched] = useState(false);
    const drag = useRef<{ x: number; left: number } | null>(null);
    const reduceMotion = useReducedMotion();

    const measure = () => {
        const el = scroller.current;
        if (el) setScroll({ left: el.scrollLeft, max: el.scrollWidth - el.clientWidth, visible: el.clientWidth / el.scrollWidth });
    };
    useEffect(measure, []);

    // Demo loop: across to the status columns, a pause, then back
    useEffect(() => {
        const el = scroller.current;
        if (!el || !show || touched || reduceMotion) return;
        let cancelled = false;
        let current: ReturnType<typeof animate> | undefined;
        const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
        const glide = (to: number) =>
            new Promise<void>((resolve) => {
                current = animate(el.scrollLeft, to, { duration: 3.2, ease: [0.65, 0, 0.35, 1], onUpdate: (v) => (el.scrollLeft = v), onComplete: resolve });
            });
        (async () => {
            await wait(2600);
            while (!cancelled) {
                await glide(el.scrollWidth - el.clientWidth);
                if (cancelled) break;
                await wait(2800);
                if (cancelled) break;
                await glide(0);
                if (cancelled) break;
                await wait(3200);
            }
        })();
        return () => {
            cancelled = true;
            current?.stop();
        };
    }, [show, touched, reduceMotion]);

    const takeOver = () => setTouched(true);
    const progress = scroll.max > 0 ? scroll.left / scroll.max : 0;
    const fade = (on: boolean) => (on ? "opacity-100" : "opacity-0");

    return (
        <div className="absolute left-12 top-[370px] h-[528px] w-[1099px] overflow-hidden rounded-[32px] border border-zinc-200 bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.18)] dark:border-white/[0.07] dark:bg-[#0D0C19] dark:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
            <div
                ref={scroller}
                className="h-full cursor-grab select-none overflow-x-auto overflow-y-hidden [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden"
                onScroll={measure}
                onWheel={(e) => Math.abs(e.deltaX) > Math.abs(e.deltaY) && takeOver()}
                onTouchStart={takeOver}
                onPointerDown={(e) => {
                    takeOver();
                    if (e.pointerType === "mouse") drag.current = { x: e.clientX, left: e.currentTarget.scrollLeft };
                }}
                onPointerMove={(e) => {
                    if (!drag.current) return;
                    // Pointer moves are in screen pixels; the table lives on the scaled artboard
                    e.currentTarget.scrollLeft = drag.current.left - (e.clientX - drag.current.x) / scale;
                }}
                onPointerUp={() => (drag.current = null)}
                onPointerLeave={() => (drag.current = null)}
            >
                <div className="w-max pb-3">
                    <div className="flex h-[68px] items-center px-6 text-[14px] font-medium text-zinc-500 dark:text-slate-500">
                        {COLUMNS.map((c) => (
                            <Cell key={c.label} width={c.width}>{c.label}</Cell>
                        ))}
                    </div>
                    {PATIENTS.map((p, i) => (
                        <PatientRow key={p.name} p={p} index={i} show={show} />
                    ))}
                </div>
            </div>

            {/* Edge fades show there is more to either side */}
            <div aria-hidden className={`pointer-events-none absolute inset-y-0 left-0 w-56 bg-gradient-to-r from-white to-transparent transition-opacity duration-300 dark:from-[#0D0C19] ${fade(progress > 0.02)}`} />
            <div aria-hidden className={`pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-white to-transparent transition-opacity duration-300 dark:from-[#0D0C19] ${fade(progress < 0.98)}`} />

            {/* Our own scrollbar, so it looks like the design and not the OS */}
            <div aria-hidden className="pointer-events-none absolute inset-x-2 bottom-2 h-[7px] rounded-full">
                <div
                    className="h-full rounded-full bg-zinc-300 dark:bg-slate-700"
                    style={{ width: `${scroll.visible * 100}%`, transform: `translateX(${progress * (1 / scroll.visible - 1) * 100}%)` }}
                />
            </div>
        </div>
    );
}

function Screen({ show, scale }: { show: boolean; scale: number }) {
    return (
        <ScreenFrame>
            {/* Header */}
            <div className="absolute left-12 top-[58px] w-[820px]">
                <h3 className="text-[40px] font-medium leading-tight text-zinc-900 dark:text-slate-100">Patient Management</h3>
                <p className="mt-2.5 text-[20px] font-light leading-[1.65] text-zinc-500 dark:text-slate-500">
                    Manage your clinic's patients: add and invite patients, keep their details up to date, and organize them into groups.
                </p>
            </div>

            <Clicky className="absolute left-[992px] top-[93px] flex h-[54px] w-40 items-center justify-center gap-3 overflow-hidden rounded-full bg-[linear-gradient(90deg,#7316D0,#8C00E2_27%,#5021FB)] text-[16px] font-semibold text-white transition-[filter] hover:brightness-110">
                <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-t from-black/20 to-white/20" />
                <UserPlus className="relative size-6" strokeWidth={1.6} />
                <span className="relative">Add Patient</span>
            </Clicky>

            {/* Tabs */}
            <div className="absolute left-12 right-12 top-[228px] flex gap-10 border-b border-zinc-200 text-[22px] dark:border-slate-800">
                <Clicky ring="rounded-lg" className="pb-3 font-medium text-zinc-900 dark:text-white">
                    Patients
                    <span className="absolute -bottom-px left-0 right-0 h-[2px] rounded-full bg-[linear-gradient(90deg,#F725B1,#8E0EF0_60%,#00ECED_140%)]" />
                </Clicky>
                <Clicky ring="rounded-lg" className="pb-3 text-zinc-400 transition-colors hover:text-zinc-700 dark:text-slate-500 dark:hover:text-slate-300">
                    Patient Groups
                </Clicky>
            </div>

            {/* Search and filters */}
            <div className="absolute left-12 top-[303px] flex h-9 items-center gap-2">
                <Clicky ring="rounded-full" className="mr-[50px] flex h-9 w-[266px] items-center gap-2 rounded-full border border-zinc-300 bg-white px-5 text-[16px] text-zinc-400 transition-colors hover:border-zinc-400 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-500 dark:hover:border-slate-500">
                    <Search className="size-4" />
                    Search name or email
                </Clicky>
                {FILTERS.map((f) => (
                    <Clicky
                        key={f}
                        className="group/chip flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 text-[16px] text-zinc-600 transition-colors hover:border-purple-400 hover:text-zinc-900 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:border-purple-400/60 dark:hover:text-white"
                    >
                        {f}
                        <ChevronDown className="size-4 transition-transform duration-200 group-hover/chip:translate-y-0.5" />
                    </Clicky>
                ))}
            </div>

            <PatientTable show={show} scale={scale} />
        </ScreenFrame>
    );
}

export function PatientManagementMock({ reserve }: { reserve?: string }) {
    return (
        <ScreenStage reserve={reserve} label="Quilt Pro patient management screen: a searchable, filterable list of patients with their conditions, tags, groups, sign-up and account status.">
            {({ scale, inView }) => <Screen show={inView} scale={scale} />}
        </ScreenStage>
    );
}

export default PatientManagementMock;
